import axios, { type InternalAxiosRequestConfig } from 'axios';
import { acquireToken, tokenRef } from '../auth/tokenRef';
import { redirectToLogin } from '../auth/redirect';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 30_000
});

// Ref-pattern interceptor. Reads tokenRef.current at send time -- never
// captures a stale token in a closure. Pattern per ADR 0003. If the ref
// hasn't been populated yet (e.g. direct nav to a route that queries before
// AuthSync's fire-and-forget acquire completes), acquire on demand here.
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  let token = tokenRef.current;
  if (!token) {
    try {
      token = await acquireToken(null);
    } catch {
      // Swallow: let the request proceed unauthenticated so the server
      // returns 401 and the response interceptor below routes to login.
    }
  }
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const response = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
    const status = response?.status;
    if (status === 401) {
      // Token-side state is stale: drop it, drop the MSAL cache for the
      // active account, and bounce the user to login preserving the page
      // they were on. redirectToLogin is internally idempotent across
      // parallel 401s.
      void redirectToLogin();
    } else if (status === 402 && response?.data?.error === 'subscription_inactive') {
      // A data endpoint rejected a lapsed tenant. Route to /signup, which
      // re-reads billing status and re-enters checkout. Guard against loops:
      // the billing endpoints themselves are gate-exempt (never 402), and we
      // don't re-navigate if we're already on the signup flow.
      const requestUrl = (error as { config?: { url?: string } })?.config?.url ?? '';
      const isBillingCall = requestUrl.includes('/api/v1/billing/');
      if (!isBillingCall && window.location.pathname !== '/signup') {
        window.location.assign('/signup');
      }
    }
    return Promise.reject(error);
  }
);
